/**
 * Requirements summary:
 * Centralizes class schedule instructor fallback rules so new multi-instructor
 * slots and legacy single-instructor slots behave the same across forms, APIs,
 * conflict checks, and schedule displays.
 */

import { isMissingRelationError } from '@/utils/supabase/errorGuards';

export interface InstructorAssignableSlot {
  instructor_id?: string | null;
  instructor_ids?: Array<string | null | undefined> | null;
}

export interface InstructorNameRecord {
  id?: string | null;
  preferred_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  auth_user?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  auth_users?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

export function normalizeClassScheduleInstructorIds(
  slot: InstructorAssignableSlot | null | undefined,
  fallbackInstructorId?: string | null
): string[] {
  const hasExplicitInstructorIds = Array.isArray(slot?.instructor_ids);
  const ids = hasExplicitInstructorIds
    ? slot?.instructor_ids || []
    : [
        slot?.instructor_id,
        fallbackInstructorId,
      ];

  const seen = new Set<string>();
  const normalized: string[] = [];

  ids.forEach((rawId) => {
    const id = typeof rawId === 'string' ? rawId.trim() : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    normalized.push(id);
  });

  return normalized;
}

export function getPrimaryClassScheduleInstructorId(
  slot: InstructorAssignableSlot | null | undefined,
  fallbackInstructorId?: string | null
): string | null {
  return normalizeClassScheduleInstructorIds(slot, fallbackInstructorId)[0] || null;
}

export function instructorIdsForAssignmentRows(
  slot: InstructorAssignableSlot | null | undefined,
  fallbackInstructorId?: string | null
): string[] {
  return normalizeClassScheduleInstructorIds(slot, fallbackInstructorId);
}

export function instructorIdsForHydratedSchedule(
  slot: (InstructorAssignableSlot & { instructor_assignments?: Array<{ instructor_id?: string | null }> | null }) | null | undefined,
  fallbackInstructorId?: string | null
): string[] {
  const assignmentIds = (slot?.instructor_assignments || []).map((assignment) => assignment.instructor_id);

  return normalizeClassScheduleInstructorIds({
    instructor_ids: assignmentIds.length > 0 ? assignmentIds : undefined,
    instructor_id: slot?.instructor_id || fallbackInstructorId,
  });
}

export async function validateClassScheduleInstructorIds(
  supabase: any,
  organizationId: string,
  instructorIds: string[]
): Promise<{ valid: boolean; invalidIds: string[] }> {
  const uniqueIds = normalizeClassScheduleInstructorIds({ instructor_ids: instructorIds });
  if (uniqueIds.length === 0) return { valid: true, invalidIds: [] };

  const { data, error } = await supabase
    .from('organization_staff')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .in('id', uniqueIds);

  if (error) {
    throw error;
  }

  const foundIds = new Set((data || []).map((staff: any) => staff.id));
  const invalidIds = uniqueIds.filter((id) => !foundIds.has(id));

  return {
    valid: invalidIds.length === 0,
    invalidIds,
  };
}

export function formatClassScheduleInstructorName(instructor: InstructorNameRecord | null | undefined): string | null {
  if (!instructor) return null;
  if (instructor.preferred_name?.trim()) return instructor.preferred_name.trim();

  const firstName =
    instructor.first_name ||
    instructor.auth_user?.first_name ||
    instructor.auth_users?.first_name ||
    '';
  const lastName =
    instructor.last_name ||
    instructor.auth_user?.last_name ||
    instructor.auth_users?.last_name ||
    '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || null;
}

export function joinClassScheduleInstructorNames(names: Array<string | null | undefined>): string | null {
  const uniqueNames = names.reduce<string[]>((acc, rawName) => {
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    if (name && !acc.includes(name)) acc.push(name);
    return acc;
  }, []);

  return uniqueNames.length > 0 ? uniqueNames.join(', ') : null;
}

/**
 * True only for the "class_schedule_instructors table/relationship doesn't
 * exist yet" error (a not-yet-migrated assignment table). Composes the shared
 * generic missing-relation guard with this table's name so unrelated
 * missing-relation errors elsewhere never trigger this table's fallback path.
 *
 * Keeps the two PostgREST relationship/table phrases ("could not find a
 * relationship" / "could not find the table") that the generic guard doesn't
 * carry, so behavior for this table is preserved exactly.
 */
export function isMissingClassScheduleInstructorsRelation(error: any): boolean {
  if (!error) return false;
  const message = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`.toLowerCase();
  if (!message.includes('class_schedule_instructors')) return false;

  return (
    isMissingRelationError(error) ||
    message.includes('could not find a relationship') ||
    message.includes('could not find the table')
  );
}
